import { http } from "./http";
import type { PaymentMethod } from "@/types";

export type AmountsByMethod = Partial<Record<PaymentMethod, number>>;

/* -------------------------------------------------------------------------- */
/* Caisse du jour                                                              */
/* -------------------------------------------------------------------------- */

export interface CashOperation {
  kind: "RECU" | "RECETTE";
  id: number;
  /** N° de reçu, ou catégorie de la recette. */
  reference: string;
  label: string;
  method: PaymentMethod;
  amount: number;
  cashierKey: string;
  cashierName: string;
  createdAt: string;
  cancelled: boolean;
  cancelReason: string | null;
}

export interface CashClosing {
  id: number;
  expected: AmountsByMethod;
  counted: AmountsByMethod;
  /** Compté − attendu (négatif = manquant). */
  gap: number;
  comment: string | null;
  validatedByName: string;
  createdAt: string;
}

export interface CashierDay {
  cashierKey: string;
  cashierId: number | null;
  cashierName: string;
  expected: AmountsByMethod;
  total: number;
  operations: number;
  cancelled: number;
  closing: CashClosing | null;
}

export interface CashDay {
  date: string;
  monthClosed: boolean;
  cashiers: CashierDay[];
  totals: AmountsByMethod;
  total: number;
  operations: CashOperation[];
}

export function getCashDay(date: string): Promise<CashDay> {
  return http.get<CashDay>("/cash/day", { date });
}

export function validateCash(input: {
  date: string;
  cashierKey: string;
  counted: AmountsByMethod;
  comment?: string;
}): Promise<CashierDay> {
  return http.post<CashierDay>("/cash/validate", { ...input, comment: input.comment?.trim() || undefined });
}

export async function reopenCash(closingId: number, reason: string): Promise<void> {
  await http.post<null>(`/cash/closings/${closingId}/reopen`, { reason: reason.trim() });
}

/* -------------------------------------------------------------------------- */
/* Clôtures mensuelles                                                         */
/* -------------------------------------------------------------------------- */

export interface MonthClosing {
  /** AAAA-MM */
  month: string;
  /** « octobre 2026 » */
  label: string;
  status: "OPEN" | "CLOSED";
  closedAt: string | null;
  closedByName: string | null;
  revenues: number;
  expenses: number;
  balance: number;
  receipts: number;
  /** Jours où au moins une caisse n'a pas été comptée et validée. */
  pendingCashDays: string[];
  reopenCount: number;
}

export interface ClosingEvent {
  id: number;
  action: "CLOSE" | "REOPEN";
  reason: string | null;
  byName: string;
  createdAt: string;
}

export function getClosings(schoolYear?: string): Promise<MonthClosing[]> {
  return http.get<MonthClosing[]>("/closings", { schoolYear });
}

export function closeMonth(month: string): Promise<MonthClosing> {
  return http.post<MonthClosing>("/closings", { month });
}

export function reopenMonth(month: string, reason: string): Promise<MonthClosing> {
  return http.post<MonthClosing>(`/closings/${month}/reopen`, { reason: reason.trim() });
}

export function getClosingHistory(month: string): Promise<ClosingEvent[]> {
  return http.get<ClosingEvent[]>(`/closings/${month}/history`);
}
